import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Check } from "lucide-react";
import LogoSmall from "~/icons/LogoSmall";
import { useCompanyGroups } from "~/api/swr/useCompanyGroups";
import { switchCompanyGroup } from "~/api/v2/requests";
import { type CompanyGroup } from "~/api/types";
import { cn } from "~/lib/utils";
import { CompanyGroupLogo } from "~/molecules/CompanyGroupLogo";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/atoms/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";

export const CompanyGroupSwitcher = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: groups } = useCompanyGroups();
  const [open, setOpen] = useState(false);

  const activeGroup = groups?.find((group) => group.isActive);

  const logo = activeGroup?.logoUrl ? (
    <img
      src={activeGroup.logoUrl}
      alt={activeGroup.name}
      className="size-8 object-contain"
    />
  ) : (
    <LogoSmall height={32} width={32} />
  );

  if (!groups || !activeGroup) return logo;

  const switchableGroups = groups.filter(
    (group) => group.canSwitch || group.isActive,
  );

  const onShowOrganisations = () => {
    setOpen(false);
    void navigate("/organisations");
  };

  const onSelectGroup = async (group: CompanyGroup) => {
    setOpen(false);
    if (group.id === activeGroup.id) return;
    try {
      await switchCompanyGroup(group.id);
      const stayOnPath = ["/chart", "/documents"].includes(
        window.location.pathname,
      );
      window.location.assign(stayOnPath ? window.location.pathname : "/");
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="hover:bg-muted flex flex-col items-center rounded-md p-1">
          {logo}
          <span
            title={activeGroup.name}
            className="text-muted-foreground max-w-28 truncate text-xs"
          >
            {activeGroup.name}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-64 p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t(($) => $.commons.search)}
            className="h-9"
          />
          <CommandList showScrollbar>
            <CommandEmpty>{t(($) => $.commons.noItems)}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={t(($) => $.companyGroups.showOrganisations)}
                onSelect={onShowOrganisations}
              >
                {t(($) => $.companyGroups.showOrganisations)}
              </CommandItem>
              <CommandSeparator />
              {switchableGroups.map((group) => (
                <CommandItem
                  key={group.id}
                  value={`${group.name}-${group.id}`}
                  keywords={[group.name]}
                  onSelect={() => void onSelectGroup(group)}
                >
                  <CompanyGroupLogo
                    group={group}
                    className="size-7 rounded-full text-[10px]"
                  />
                  <span className="flex-1 whitespace-nowrap">{group.name}</span>
                  <Check
                    className={cn(
                      "size-4",
                      group.isActive ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
